import { batch } from 'react-redux';
import { APP_WILL_MOUNT, APP_WILL_UNMOUNT } from '../base/app/actionTypes';
import { CONFERENCE_JOINED, CONFERENCE_JOIN_IN_PROGRESS, CONFERENCE_WILL_LEAVE } from '../base/conference/actionTypes';
import { getConferenceState, getCurrentConference } from '../base/conference/functions';
import { openDialog } from '../base/dialog/actions';
import { JitsiConferenceEvents } from '../base/lib-jitsi-meet';
import { PARTICIPANT_JOINED, PARTICIPANT_LEFT } from '../base/participants/actionTypes';
import { participantUpdated } from '../base/participants/actions';
import { getLocalParticipant, getParticipantById, getParticipantDisplayName, isScreenShareParticipant } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import StateListenerRegistry from '../base/redux/StateListenerRegistry';
import { playSound } from '../base/sounds/actions';
import { BUTTON_TYPES } from '../base/ui/constants.any';
import { hideNotification, showNotification, showWarningNotification } from '../notifications/actions';
import { NOTIFICATION_ICON, NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { openSettingsDialog } from '../settings/actions.web';
import { SETTINGS_TABS } from '../settings/constants';
import { AUTHKEY_AKE_FINAL, AUTHKEY_AKE_FINISH, AUTHKEY_AKE_RESPONSE, AUTHKEY_START_AKE, PARTICIPANT_VERIFIED, SET_E2EE_ROOM_OWNER, SET_MEDIA_ENCRYPTION_KEY, START_VERIFICATION, TOGGLE_E2EE } from './actionTypes';
import { finishAuthKeyAKE, sendAKEFinalMessage, sendAKEResponseMessage, setE2EEMaxMode, setE2EERoomOwner, startAuthKeyAKE, toggleE2EE } from './actions';
import ParticipantVerificationDialog from './components/ParticipantVerificationDialog';
import { AUTHKEY_MESSAGE_TYPE, AUTHKEY_ROOM_OWNER_COMMAND, E2EE_OFF_SOUND_ID, E2EE_ON_SOUND_ID, MAX_MODE } from './constants';
import { applyAuthKeyEncryptionKey, assignE2EERoomOwnerIfNeeded, broadcastE2EERoomOwner, ensureAuthKeyService, ensureAuthKeyServiceReady, extractAKEsessionKey, finishAKEwithParticipant, getAuthKeyAuthenticator, getJoinedConference, getLocalAuthenticationStatus, 
// Automatic authentication for a remote participant joining is intentionally disabled.
// handleAuthKeyOnConferenceJoined,
// handleAuthKeyOnRemoteParticipantJoined,
handleE2EERoomOwnerCommand, isAuthKeyE2EEModerator, isCredentialComplete, isExternallyManagedAuthKey, isMaxModeReached, isMaxModeThresholdReached, registerE2eeAudioFiles, responseAKEwithModerator, showAuthKeyCredentialPromptOnConferenceJoined, startAKEwithParticipant, transferE2EERoomOwnerOnLeave, unregisterE2eeAudioFiles } from './functions';
import logger from './logger';
let authKeyMessageListenerConference;
MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    const conference = getCurrentConference(getState);
    switch (action.type) {
        case APP_WILL_MOUNT:
            registerE2eeAudioFiles(dispatch);
            break;
        case APP_WILL_UNMOUNT:
            unregisterE2eeAudioFiles(dispatch);
            break;
        case CONFERENCE_JOIN_IN_PROGRESS: {
            const joiningConference = action.conference;
            joiningConference?.addCommandListener?.(AUTHKEY_ROOM_OWNER_COMMAND, ({ attributes }, senderId) => {
                handleE2EERoomOwnerCommand(attributes ?? {}, senderId, dispatch, getState);
            });
            break;
        }
        case SET_E2EE_ROOM_OWNER: {
            const result = next(action);
            if (action.broadcast !== false
                && isExternallyManagedAuthKey(getState())
                && action.ownerId) {
                broadcastE2EERoomOwner(getCurrentConference(getState()), action.ownerId);
            }
            return result;
        }
        case CONFERENCE_JOINED: {
            const joinedConference = action.conference ?? getCurrentConference(getState());
            // Assign AuthKey room owner before other middleware syncs XMPP moderator role.
            assignE2EERoomOwnerIfNeeded(dispatch, getState, joinedConference);
            const result = next(action);
            ensureAuthKeyService();
            void ensureAuthKeyServiceReady().catch(error => {
                logger.error('[authkey] WASM preload failed', error);
            });
            addAuthKeyMessageListener(dispatch, joinedConference);
            [500, 2000].forEach(delay => {
                window.setTimeout(() => {
                    const currentConference = getCurrentConference(getState());
                    if (currentConference) {
                        assignE2EERoomOwnerIfNeeded(dispatch, getState, currentConference);
                        showAuthKeyCredentialPromptOnConferenceJoined(dispatch, getState);
                    }
                }, delay);
            });
            // Do not call setupAuthKey here — swapping to the AuthKey key during join
            // breaks ICE before the conference is stable. Sync credentials into WASM only;
            // the real AuthKey key is applied when E2EE is toggled on or credentials are saved in-meeting.
            if (isCredentialComplete()) {
                const localParticipant = getLocalParticipant(getState());
                const authenticator = getAuthKeyAuthenticator();
                authenticator?.setLocalCredentialInfo(localParticipant?.email ?? '', localParticipant?.credential ?? '', localParticipant?.credentialPassword ?? '');
            }
            else {
                dispatch(showNotification({
                    titleKey: 'notify.e2ee',
                    descriptionKey: 'notify.e2eeDescription',
                    customActionNameKey: ['e2ee.setCredential'],
                    customActionType: [BUTTON_TYPES.PRIMARY],
                    customActionHandler: [() => batch(() => dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE)))],
                    icon: NOTIFICATION_ICON.MESSAGE
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            _updateMaxMode(dispatch, getState);
            showAuthKeyCredentialPromptOnConferenceJoined(dispatch, getState);
            return result;
        }
        case CONFERENCE_WILL_LEAVE: {
            dispatch(setE2EERoomOwner(undefined, { broadcast: false }));
            break;
        }
        case PARTICIPANT_JOINED: {
            const result = next(action);
            const joinedConference = getCurrentConference(getState());
            if (joinedConference) {
                assignE2EERoomOwnerIfNeeded(dispatch, getState, joinedConference);
            }
            if (!isScreenShareParticipant(action.participant) && !action.participant.local) {
                _updateMaxMode(dispatch, getState);
                // Do not automatically authenticate a remote participant when they join.
                // void handleAuthKeyOnRemoteParticipantJoined(dispatch, getState, action.participant);
            }
            return result;
        }
        case PARTICIPANT_LEFT: {
            const participant = getParticipantById(getState(), action.participant?.id);
            transferE2EERoomOwnerOnLeave(dispatch, getState, action.participant?.id);
            const result = next(action);
            if (!isScreenShareParticipant(participant)) {
                _updateMaxMode(dispatch, getState);
            }
            return result;
        }
        case TOGGLE_E2EE: {
            if (action.enabled && !isCredentialComplete()) {
                dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE));
                return;
            }
            if (action.enabled && !isAuthKeyE2EEModerator(getState())) {
                dispatch(showWarningNotification({
                    titleKey: 'notify.e2ee',
                    descriptionKey: 'notify.e2eeOnlyModerator',
                    icon: NOTIFICATION_ICON.WARNING
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                return;
            }
            const joinedConference = getJoinedConference(getState);
            if (!joinedConference) {
                logger.warn('[authkey] E2EE toggle skipped: conference not fully joined yet');
                return;
            }
            const runToggle = () => {
                if (joinedConference.isE2EESupported()
                    && joinedConference.isE2EEEnabled() !== action.enabled) {
                    logger.debug(`E2EE will be ${action.enabled ? 'enabled' : 'disabled'}`);
                    joinedConference.toggleE2EE(action.enabled);
                    const participant = getLocalParticipant(getState);
                    dispatch(participantUpdated({
                        e2eeEnabled: action.enabled,
                        id: participant?.id ?? '',
                        local: true
                    }));
                    dispatch(playSound(action.enabled ? E2EE_ON_SOUND_ID : E2EE_OFF_SOUND_ID));
                    if (action.enabled) {
                        dispatch(showNotification({
                            titleKey: 'notify.e2ee',
                            descriptionKey: 'notify.e2eeEnabled',
                            icon: NOTIFICATION_ICON.SUCCESS
                        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                    }
                }
            };
            if (action.enabled && isCredentialComplete() && !getLocalAuthenticationStatus()) {
                // Enable E2EE first (one media restart), then apply the AuthKey key in place.
                // Setting the key before enable and again on enable caused a double key swap + ICE failure.
                // Skip generating a new key when the session key was already established via AKE.
                runToggle();
                applyAuthKeyEncryptionKey(getState)
                    .catch(error => logger.error('[authkey] E2EE key apply failed', error));
            }
            else if (action.enabled) {
                runToggle();
            }
            else {
                runToggle();
            }
            break;
        }
        case SET_MEDIA_ENCRYPTION_KEY: {
            const state = getState();
            const joinedConference = getConferenceState(state).conference;
            const { e2ee = {} } = state['features/base/config'];
            if (joinedConference?.isE2EESupported()) {
                const { exportedKey, index } = action.keyInfo;
                if (exportedKey) {
                    window.crypto.subtle.importKey('raw', new Uint8Array(exportedKey), 'AES-GCM', false, ['encrypt', 'decrypt'])
                        .then(encryptionKey => {
                        joinedConference.setMediaEncryptionKey({
                            encryptionKey,
                            index
                        });
                        if (e2ee.externallyManagedKey && !joinedConference.isE2EEEnabled()) {
                            joinedConference.toggleE2EE(true);
                            const localParticipant = getLocalParticipant(getState());
                            dispatch(participantUpdated({
                                e2eeEnabled: true,
                                id: localParticipant?.id ?? '',
                                local: true
                            }));
                        }
                    })
                        .catch(error => logger.error('SET_MEDIA_ENCRYPTION_KEY error', error));
                }
                else {
                    joinedConference.setMediaEncryptionKey({
                        encryptionKey: false,
                        index
                    });
                }
            }
            break;
        }
        case PARTICIPANT_VERIFIED: {
            const { isVerified, pId } = action;
            conference?.markParticipantVerified(pId, isVerified);
            break;
        }
        case START_VERIFICATION: {
            conference?.startVerification(action.pId);
            break;
        }
        case AUTHKEY_START_AKE:
            void startAKEwithParticipant(dispatch, action.pId);
            break;
        case AUTHKEY_AKE_RESPONSE:
            void responseAKEwithModerator(dispatch, action.pId, action.akeMessage);
            break;
        case AUTHKEY_AKE_FINAL:
            void finishAKEwithParticipant(dispatch, action.pId, action.akeMessage);
            break;
        case AUTHKEY_AKE_FINISH:
            void extractAKEsessionKey(dispatch, action.pId, action.akeMessage);
            break;
    }
    return next(action);
});
StateListenerRegistry.register(state => getCurrentConference(state), (conference, { dispatch }, previousConference) => {
    if (previousConference) {
        dispatch(toggleE2EE(false));
    }
    if (conference) {
        conference.on(JitsiConferenceEvents.E2EE_VERIFICATION_AVAILABLE, (pId) => {
            dispatch(participantUpdated({
                e2eeVerificationAvailable: true,
                id: pId
            }));
        });
        conference.on(JitsiConferenceEvents.E2EE_VERIFICATION_READY, (pId, sas) => {
            dispatch(openDialog(ParticipantVerificationDialog, { pId,
                sas }));
        });
        conference.on(JitsiConferenceEvents.E2EE_VERIFICATION_COMPLETED, (pId, success, message) => {
            if (message) {
                logger.warn('E2EE_VERIFICATION_COMPLETED warning', message);
            }
            dispatch(participantUpdated({
                e2eeVerified: success,
                id: pId
            }));
        });
    }
});
function _updateMaxMode(dispatch, getState) {
    const state = getState();
    const { e2ee = {} } = state['features/base/config'];
    if (e2ee.externallyManagedKey) {
        return;
    }
    const { maxMode, enabled } = state['features/e2ee'];
    const isMaxModeThresholdReachedValue = isMaxModeThresholdReached(state);
    let newMaxMode;
    if (isMaxModeThresholdReachedValue) {
        newMaxMode = MAX_MODE.THRESHOLD_EXCEEDED;
    }
    else if (isMaxModeReached(state)) {
        newMaxMode = MAX_MODE.ENABLED;
    }
    else {
        newMaxMode = MAX_MODE.DISABLED;
    }
    if (maxMode !== newMaxMode) {
        dispatch(setE2EEMaxMode(newMaxMode));
    }
    if (isMaxModeThresholdReachedValue && !enabled) {
        dispatch(toggleE2EE(false));
    }
}
function addAuthKeyMessageListener(dispatch, conference) {
    if (!conference) {
        logger.error('[authkey] Register authkey listener failed: No conference object found');
        return;
    }
    if (authKeyMessageListenerConference === conference) {
        return;
    }
    authKeyMessageListenerConference = conference;
    conference.on(JitsiConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, (...args) => {
        if (args && args.length > 1) {
            const [sender, eventData] = args;
            switch (eventData.type) {
                case AUTHKEY_MESSAGE_TYPE.AKE_REQUEST: {
                    if (isAuthKeyE2EEModerator(APP.store.getState())) {
                        const senderDisplayName = getParticipantDisplayName(APP.store.getState(), sender._id);
                        dispatch(showNotification({
                            uid: `${sender._id}-ake-request`,
                            titleKey: 'notify.e2ee',
                            descriptionKey: 'e2ee.authRequestReceived',
                            descriptionArguments: { name: senderDisplayName },
                            customActionNameKey: ['e2ee.authenticate', 'e2ee.ignore'],
                            customActionType: [BUTTON_TYPES.PRIMARY, BUTTON_TYPES.DESTRUCTIVE],
                            customActionHandler: [
                                () => batch(() => dispatch(startAuthKeyAKE(sender._id))),
                                () => batch(() => dispatch(hideNotification(`${sender._id}-ake-request`)))
                            ],
                            icon: NOTIFICATION_ICON.MESSAGE
                        }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
                    }
                    break;
                }
                case AUTHKEY_MESSAGE_TYPE.AKE_INIT: {
                    if (isCredentialComplete()) {
                        dispatch(sendAKEResponseMessage(sender._id, eventData.payload));
                    }
                    else {
                        dispatch(showWarningNotification({
                            titleKey: 'notify.e2ee',
                            descriptionKey: 'notify.e2eeCredentialIncomplete',
                            icon: NOTIFICATION_ICON.WARNING
                        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                        dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE));
                    }
                    break;
                }
                case AUTHKEY_MESSAGE_TYPE.AKE_RESPONSE:
                    dispatch(sendAKEFinalMessage(sender._id, eventData.payload));
                    break;
                case AUTHKEY_MESSAGE_TYPE.AKE_FINAL:
                    dispatch(finishAuthKeyAKE(sender._id, eventData.payload));
                    break;
                case AUTHKEY_MESSAGE_TYPE.AKE_END:
                    break;
                case AUTHKEY_MESSAGE_TYPE.AKE_ERROR:
                    logger.error('[authkey] Received AUTHKEY_AKE_ERROR', eventData);
                    break;
                default:
                    break;
            }
        }
    });
}
